import { widgetBusinessFetch } from "./business-fetch";
import {
  groundingSchema,
  replyStatesSchema,
  replyCompareCardSchema,
  replyProductCardSchema,
  replySizeCardSchema,
} from "@go-novus/admin-api";
import { z } from "zod";

// 历史行是回复帧换了个信封：挂件补回的答案和实时回复必须是同一个形状。
// 用 looseObject：这条响应还带 created_at、product_context、next_cursor，
// 挂件今天用不上，但数据是逐层传下来的，中间层没资格替下游把它们删掉。
const historySchema = z.looseObject({
  messages: z.array(
    z.looseObject({
      message_id: z.string(),
      conversation_id: z.string(),
      role: z.enum(["user", "assistant", "quick_response"]),
      content: z.string(),
      product_cards: z.array(replyProductCardSchema).optional(),
      size_card: replySizeCardSchema.nullish(),
      compare_card: replyCompareCardSchema.nullish(),
      grounding: groundingSchema.nullish(),
      reply_states: replyStatesSchema.optional(),
    }),
  ),
  next_cursor: z.string().nullable().optional(),
});

export type HistoryMessage = z.infer<typeof historySchema>["messages"][number];

/** 历史只按 uid 读取，浏览器到 BFF 只发 POST 请求体。 */
async function fetchConversationHistory(
  handshakeUrl: string,
  uid: string,
  conversationId: string,
  fetchImpl: typeof fetch,
): Promise<Response> {
  const url = new URL(handshakeUrl, location.href);
  url.pathname = url.pathname.replace(/\/handshake$/, "/history");
  url.searchParams.delete("uid");
  return fetchImpl(url.href, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ uid, conversation_id: conversationId }),
    signal: AbortSignal.timeout(8_000),
  });
}

/** 登录续接补读仍按当前 uid 和会话读取，不进行授权历史恢复。 */
export async function requestConversationHistory(
  handshakeUrl: string,
  uid: string,
  conversationId: string,
  fetchImpl: typeof fetch = widgetBusinessFetch(handshakeUrl),
) {
  try {
    const response = await fetchConversationHistory(
      handshakeUrl,
      uid,
      conversationId,
      fetchImpl,
    );
    if (!response.ok) return { status: "failed" as const };
    const parsed = historySchema.safeParse(await response.json());
    if (!parsed.success) return { status: "failed" as const };
    return {
      status: "ok" as const,
      messages: parsed.data.messages.filter(
        (message) => message.conversation_id === conversationId,
      ),
    };
  } catch {
    return { status: "failed" as const };
  }
}

/** 待答原话之后出现的第一条助手消息结束等待，中间态和其他原话不影响判定。 */
export async function requestPendingAnswer(
  handshakeUrl: string,
  uid: string,
  conversationId: string,
  messageId: string,
  fetchImpl: typeof fetch = widgetBusinessFetch(handshakeUrl),
) {
  try {
    const result = await requestConversationHistory(
      handshakeUrl,
      uid,
      conversationId,
      fetchImpl,
    );
    if (result.status !== "ok") return result;
    const messages = result.messages;
    const index = messages.findIndex(
      (message) => message.role === "user" && message.message_id === messageId,
    );
    const answer =
      index < 0
        ? undefined
        : messages
            .slice(index + 1)
            .find((message) => message.role === "assistant");
    if (answer) return { status: "ok" as const, answer };
    return { status: "ok" as const, answer: undefined };
  } catch {
    return { status: "failed" as const };
  }
}
